import { angleTo, dist } from '../../core/math';
import type { Actor } from '../../sim/actor';
import { type BossMechanic, type BossScript, registerBossScript } from '../../sim/ai/brains';
import { registry, type MonsterDef } from '../../sim/registry';
import { shapeHits } from '../../sim/shapes';
import type { SkillDef } from '../../sim/skillTypes';
import { registerStatus } from '../../sim/status';
import type { World } from '../../sim/world';
import { monsterStats } from '../balance';
import { circle, cone, counterWindow, hit, hits, move, proj, rect, ring, teleport } from '../classes/kit';
import {
  KD,
  ONE_SHOT,
  after,
  announce,
  around,
  behindPillar,
  bossScript,
  bskill,
  burn,
  castAt,
  chill,
  counterCharge,
  despawn,
  enc,
  every,
  exposeStagger,
  farthestPlayer,
  isPending,
  freeze,
  hazard,
  helpersOf,
  hpScale,
  kb,
  launch,
  mech,
  northOf,
  players,
  playerPoint,
  polarSquare,
  randomPlayer,
  registerEncounter,
  resetStagger,
  setStacks,
  spawnHelper,
  stacksOf,
  stillCasting,
  stun,
  tele,
  wipeCast,
  type CastOutcome,
  type EncState,
  type EncounterDef,
} from './common';

/**
 * Warlord Siege: the Hollow Throne — weekly raid, 3 gates, mechanics at HP bar
 * counts. Normal / Hard: every boss has a `<id>_hard` variant (params.hard,
 * +40% HP) whose script adds one extra mechanic per gate.
 *   Gate I   `hellhound_kharos` + `hellhound_vesh`: keep them apart (Pack Bond
 *            within 8 m), bleed stacks → Blood Line, orb colour order, freezing
 *            shards, colour-read claws. Hard: Last Howl stagger check.
 *   Gate II  `vhalzeroth`: ×160 armour break (bait charges into pillars),
 *            ×130 wipe axe (orb wards), ×110 pillar hug, ×85/×35 ring breaks,
 *            ×65 triple counter check, ×50 stagger check. Hard: ×20 Imprisonment.
 *   Gate III `vhalzeroth_phantom`: phantom armour stacks removed only by
 *            countering phantom clones at the arena edge; ×10 final enrage.
 *            Hard: 6 stacks and ×15 Lantern Eclipse.
 */

const RAD = Math.PI / 180;
const DIAGONALS = [45, 135, 225, 315];
export const SIEGE_HARD_HP = 1.4;

/** Id of the Normal or Hard variant of a siege boss. */
export function siegeBossId(base: string, hard: boolean): string {
  return hard ? `${base}_hard` : base;
}

function isHard(b: Actor): boolean {
  return registry.monsters.get(b.monster!.defId)?.params?.hard === true;
}

function hardVariant(def: MonsterDef, params: Record<string, number | string | boolean> = {}): MonsterDef {
  return {
    ...def,
    id: `${def.id}_hard`,
    title: `${def.title ?? 'Warlord Siege'} (Hard)`,
    stats: (l) => {
      const s = def.stats(l);
      return { ...s, maxHp: Math.round((s.maxHp ?? 1) * SIEGE_HARD_HP) };
    },
    params: { ...def.params, ...params, hard: true, ilvl: 370 },
  };
}

// =============================================================== Gate I · The Hellhounds

const HOUND_ATK = 0.85;
const HH = hpScale('raid', HOUND_ATK);
/** Twins within this distance empower each other. */
export const PACK_BOND_RANGE = 8;

registerStatus({ id: 'pack_bond', name: 'Pack Bond', kind: 'buff', icon: 'pack_bond', description: `Its twin is within ${PACK_BOND_RANGE} m: attack +30%, damage taken -50%. Pull them apart!`, stacking: 'refresh', mods: { atkPct: 0.3, dmgReduction: 0.5 }, color: 0xff6a3a });
registerStatus({ id: 'hound_bleed', name: 'Hellhound Bleed', kind: 'debuff', icon: 'hound_bleed', description: 'Bleeding. At 3 stacks Kharos carves a Blood Line through you.', stacking: 'stack', maxStacks: 3, tick: { interval: 1, kind: 'damage' }, color: 0xc0182a });
registerStatus({ id: 'hound_sundered', name: 'Sundered Pack', kind: 'debuff', icon: 'hound_sundered', description: 'Orb order completed: damage taken +30%.', stacking: 'refresh', mods: { dmgTakenPct: 0.3 }, color: 0xffd24a });
registerStatus({ id: 'hound_grief', name: 'Grieving Fury', kind: 'buff', icon: 'hound_grief', description: 'Its twin has fallen: attack +40%, attack speed +20%.', stacking: 'refresh', mods: { atkPct: 0.4, atkSpeedPct: 0.2 }, color: 0xff2a2a });

const HOUND_SHARED: SkillDef[] = [wipeCast('hound_last_howl', 'Last Howl', 12, HH(ONE_SHOT))];

const REND_CONE = cone(4.8, 110);

const KHAROS_SKILLS: SkillDef[] = [
  bskill({ id: 'kharos_rend', name: 'Crimson Rend', cd: 6, anim: 'Attack', dur: 1.8, description: 'Three claw swipes; each landing rend adds a bleed stack.', actions: [0.7, 1.0, 1.3].map((t) => hit(t, REND_CONE, HH(0.08), { reaction: { kind: 'flinch' } })) }),
  counterCharge({ id: 'kharos_lunge', name: 'Crimson Lunge', cd: 12, distance: 14, width: 3, windup: 1.2, coef: HH(0.3), element: 'fire' }),
  bskill({ id: 'kharos_teleport_spin', name: 'Blink Spin', cd: 13, anim: 'Attack2', dur: 1.6, element: 'fire', actions: [teleport(0.5, 14), tele(0.55, circle(6), 0.7, 'red', HH(0.25), { reaction: kb(3) })] }),
  bskill({ id: 'kharos_roar_jump', name: 'Roaring Leap', cd: 15, anim: 'Roar', dur: 2.1, element: 'fire', actions: [tele(0, circle(9), 1.6, 'red', HH(0.35), { reaction: launch() })] }),
  bskill({ id: 'kharos_backflip', name: 'Scorched Backflip', cd: 10, anim: 'Leap', dur: 1.2, element: 'fire', actions: [move(0.1, -7, 0.35), hazard(0, rect(7, 3, -7), 0.5, 4, 1, HH(0.05), 'red', { status: [burn()] })] }),
  bskill({ id: 'kharos_fear', name: 'Dread Pounce', cd: 20, anim: 'Leap', dur: 2.0, element: 'fire', description: 'Blinks to the farthest player and howls (fear).', actions: [teleport(0.6, 30), tele(0.65, circle(7), 1.0, 'blue', HH(0.15), { status: [{ id: 'fear', duration: 2 }] })] }),
  bskill({ id: 'kharos_inferno_breath', name: 'Inferno Breath', cd: 11, anim: 'Breath', dur: 2.2, element: 'fire', actions: [hazard(0, cone(10, 60), 1.0, 1.0, 0.25, HH(0.07), 'red', { status: [burn()] })] }),
  // Mechanic-only.
  bskill({ id: 'kharos_blood_line', name: 'Blood Line', cd: 0, anim: 'Attack2', dur: 1.4, description: 'A line slash through a player at 3 bleed stacks.', actions: [tele(0, rect(34, 3, -4), 1.0, 'red', HH(0.6), { reaction: KD })] }),
  bskill({ id: 'kharos_twin_cataclysm', name: 'Twin Cataclysm', cd: 0, anim: 'Roar', dur: 2.4, element: 'fire', tags: ['oneshot'], description: 'Punishes a broken orb order.', actions: [tele(0, circle(50), 1.8, 'red', HH(1.6), { reaction: KD })] }),
  ...HOUND_SHARED,
];

const VESH_SKILLS: SkillDef[] = [
  bskill({ id: 'vesh_bite', name: 'Frost Bite', cd: 5, anim: 'Attack', dur: 1.5, element: 'water', actions: [tele(0, cone(4.5, 90), 0.6, 'red', HH(0.12), { reaction: { kind: 'flinch' } }), tele(0.4, cone(4.8, 100), 0.6, 'red', HH(0.12), { reaction: kb(2) })] }),
  bskill({ id: 'vesh_claw_crimson', name: 'Prismatic Claw: Crimson', cd: 14, anim: 'Slam', dur: 2.0, element: 'fire', description: 'The claw glows crimson: a huge blast — get far away.', actions: [tele(0, circle(9), 1.5, 'red', HH(0.4), { reaction: launch() })] }),
  bskill({ id: 'vesh_claw_azure', name: 'Prismatic Claw: Azure', cd: 14, anim: 'Attack2', dur: 1.6, element: 'water', description: 'The claw glows azure: freezing shards fan out.', actions: [proj(1.0, { speed: 14, range: 16, radius: 0.6, count: 7, spreadDeg: 90, hit: { shape: circle(0.6), coef: HH(0.12), status: [freeze(1.5)] } })] }),
  bskill({ id: 'vesh_claw_verdant', name: 'Prismatic Claw: Verdant', cd: 14, anim: 'Slam', dur: 2.6, description: 'The claw glows green: expanding rings.', actions: [tele(0, ring(3, 7), 1.0, 'red', HH(0.18)), tele(0, ring(7, 11), 1.6, 'red', HH(0.18)), tele(0, ring(11, 15), 2.2, 'red', HH(0.18))] }),
  counterCharge({ id: 'vesh_lunge', name: 'Azure Lunge', cd: 12, distance: 14, width: 3, windup: 1.2, coef: HH(0.3), element: 'water' }),
  bskill({ id: 'vesh_glacial_bomb', name: 'Glacial Bomb', cd: 12, anim: 'Breath', dur: 2.0, element: 'water', actions: [tele(0, circle(5), 1.6, 'blue', HH(0.3), { status: [freeze(1.5)] }, { anchor: 'point' })] }),
  bskill({ id: 'vesh_backflip', name: 'Rime Backflip', cd: 10, anim: 'Leap', dur: 1.2, element: 'water', actions: [move(0.1, -7, 0.35), hazard(0, rect(7, 3, -7), 0.5, 5, 1, HH(0.04), 'blue', { status: [chill(2)] })] }),
  bskill({ id: 'vesh_teleport_spin', name: 'Blink Spin', cd: 13, anim: 'Attack2', dur: 1.6, element: 'water', actions: [teleport(0.5, 14), tele(0.55, circle(6), 0.7, 'red', HH(0.25), { reaction: kb(3), status: [chill()] })] }),
  bskill({ id: 'vesh_blizzard_howl', name: 'Blizzard Howl', cd: 18, anim: 'Roar', dur: 1.8, element: 'water', actions: [tele(0, circle(10), 1.2, 'blue', HH(0.15), { status: [freeze(1.2)] })] }),
  // Mechanic-only.
  bskill({ id: 'vesh_shard_tempest', name: 'Shard Tempest', cd: 0, anim: 'Roar', dur: 3.0, element: 'water', description: 'Freezing shards rain in two waves.', actions: [
    ...([[5, 0], [9, 60], [13, 120], [6, 180], [11, 240], [15, 300], [16, 30], [8, 210]] as const).map(([d, a]) => tele(0, polarSquare(d, a, 3.5), 1.4, 'blue', HH(0.25), { status: [freeze(2)] })),
    ...([[7, 30], [12, 90], [5, 150], [10, 210], [14, 270], [9, 330], [16, 170], [4, 90]] as const).map(([d, a]) => tele(0, polarSquare(d, a, 3.5), 2.4, 'blue', HH(0.25), { status: [freeze(2)] })),
  ] }),
  ...HOUND_SHARED,
];

const houndOrb = (id: string, name: string, tint: number): MonsterDef => ({
  id, name, grade: 'minion', model: 'fx_orb', scale: 1, radius: 0.1, tint,
  stats: (l) => monsterStats(l, 'minion'), skills: [], brain: 'none', aggroRange: 0, leash: 99, xp: 0, loot: null,
  params: { helper: true, pickupRadius: 1.3 },
});

const KHAROS: MonsterDef = {
  id: 'hellhound_kharos', name: 'Kharos', title: 'Warlord Siege — Gate I', creature: 'Demon', grade: 'raid', model: 'boss_gloomfang', scale: 0.9, radius: 1.9, tint: 0xb3261e,
  stats: (l) => monsterStats(l, 'raid', { hp: 0.65, atk: HOUND_ATK, speed: 1.2 }),
  skills: KHAROS_SKILLS, brain: 'boss', aggroRange: 40, leash: 80, xp: 6000, loot: 'siege_g1',
  weakness: 'water', stagger: 700, hpBars: 60,
  params: { arenaRadius: 22, ilvl: 340, gate: 1, partner: 'hellhound_vesh' },
};

const VESH: MonsterDef = {
  id: 'hellhound_vesh', name: 'Vesh', title: 'Warlord Siege — Gate I', creature: 'Demon', grade: 'raid', model: 'boss_gloomfang', scale: 0.9, radius: 1.9, tint: 0x2b59c3,
  stats: (l) => monsterStats(l, 'raid', { hp: 0.65, atk: HOUND_ATK, speed: 1.2 }),
  skills: VESH_SKILLS, brain: 'boss', aggroRange: 40, leash: 80, xp: 6000, loot: 'siege_g1',
  weakness: 'fire', stagger: 700, hpBars: 60,
  params: { arenaRadius: 22, ilvl: 340, gate: 1, partner: 'hellhound_kharos' },
};

type OrbColour = 'crimson' | 'azure';
interface OrbOrder {
  seq: OrbColour[];
  step: number;
  orbs: { id: number; colour: OrbColour }[];
  deadline: number;
  done: boolean;
}

function partnerOf(w: World, b: Actor, e: EncState): Actor | null {
  const cached = w.actors.get((e.data.partnerId as number | undefined) ?? -1);
  if (cached) return cached;
  const want = String(registry.monsters.get(b.monster!.defId)?.params?.partner ?? '');
  const ids = [want, `${want}_hard`];
  let best: Actor | null = null;
  for (const a of w.actors.values()) {
    if (a === b || !a.monster || !ids.includes(a.monster.defId)) continue;
    if (!best || (a.alive && !best.alive) || (a.alive === best.alive && dist(a.pos, b.pos) < dist(best.pos, b.pos))) best = a;
  }
  if (best) e.data.partnerId = best.id;
  return best;
}

function startOrbOrder(w: World, b: Actor, e: EncState): void {
  const first: OrbColour = w.rng.chance(0.5) ? 'crimson' : 'azure';
  const second: OrbColour = first === 'crimson' ? 'azure' : 'crimson';
  const seq: OrbColour[] = [first, second, first, second];
  const angles = w.rng.shuffle([...DIAGONALS]);
  const orbs = angles.map((a, i) => {
    const colour: OrbColour = i % 2 === 0 ? 'crimson' : 'azure';
    const orb = spawnHelper(w, b, `hound_orb_${colour}`, around(e.arena.center, e.arena.radius * 0.6, a * RAD));
    return { id: orb.id, colour };
  });
  e.data.orbOrder = { seq, step: 0, orbs, deadline: e.time + 25, done: false } satisfies OrbOrder;
  announce(w, `Orb order: ${seq.join(' → ')}`, 'info');
}

function finishOrbOrder(w: World, b: Actor, e: EncState, oo: OrbOrder, ok: boolean, why = ''): void {
  oo.done = true;
  for (const o of oo.orbs) despawn(w, o.id);
  if (ok) {
    announce(w, 'Orb order complete — the hounds are sundered!', 'success');
    for (const h of [b, partnerOf(w, b, e)]) {
      if (!h?.alive) continue;
      w.grantStatus(h, { id: 'hound_sundered', duration: 15 }, b.id);
      exposeStagger(h, 0.3);
    }
  } else {
    announce(w, `Orb order broken${why} — Twin Cataclysm!`, 'warning');
    castAt(b, 'kharos_twin_cataclysm', () => northOf(b));
  }
}

function watchOrbOrder(w: World, b: Actor, e: EncState, oo: OrbOrder): void {
  if (oo.done) return;
  for (const o of oo.orbs) {
    const orb = w.actors.get(o.id);
    if (!orb?.alive) continue;
    const p = players(w).find((pl) => dist(pl.pos, orb.pos) <= 1.3);
    if (!p) continue;
    despawn(w, o.id);
    oo.orbs = oo.orbs.filter((x) => x.id !== o.id);
    if (o.colour !== oo.seq[oo.step]) {
      finishOrbOrder(w, b, e, oo, false, ` (${p.name} took ${o.colour})`);
      return;
    }
    oo.step++;
    announce(w, `${o.colour} orb claimed (${oo.step}/${oo.seq.length})`, 'info');
  }
  if (oo.step >= oo.seq.length) finishOrbOrder(w, b, e, oo, true);
  else if (e.time > oo.deadline) finishOrbOrder(w, b, e, oo, false, ' (too slow)');
}

function addBleed(w: World, b: Actor, e: EncState, p: Actor): void {
  const counts = (e.data.bleed as Record<number, number> | undefined) ?? {};
  e.data.bleed = counts;
  const n = (counts[p.id] ?? 0) + 1;
  w.grantStatus(p, { id: 'hound_bleed', duration: 10, stacks: 1, value: 0.08 }, b.id);
  if (n >= 3) {
    counts[p.id] = 0;
    announce(w, `${p.name} is marked by the Blood Line!`, 'warning');
    castAt(b, 'kharos_blood_line', playerPoint(w, b, p.id));
    after(b, 3, () => w.revokeStatus(p, ['hound_bleed'], b.id));
  } else counts[p.id] = n;
}

function houndEncounter(kharos: boolean): EncounterDef {
  return {
    clearOnDeath: kharos ? ['hound_bleed'] : [],
    watch: (w, b, e) => {
      const partner = partnerOf(w, b, e);
      // Pack Bond: the twins empower each other when close together.
      const close = !!partner?.alive && dist(partner.pos, b.pos) <= PACK_BOND_RANGE;
      if (close) {
        if (!b.hasStatus('pack_bond') || every(e, 'bond', 0.25)) w.grantStatus(b, { id: 'pack_bond', duration: 0.6 }, b.id);
      } else if (b.hasStatus('pack_bond')) {
        w.revokeStatus(b, ['pack_bond'], b.id);
      }
      // Hard: the survivor grieves and must be staggered before its Last Howl.
      if (isHard(b) && partner && !partner.alive && !e.data.grief) {
        e.data.grief = true;
        e.log.push('Last Howl');
        w.grantStatus(b, { id: 'hound_grief', duration: 1e9 }, b.id);
        resetStagger(b, 1);
        announce(w, `${b.name} howls for its fallen twin — stagger it before the Last Howl!`, 'mechanic');
        castAt(b, 'hound_last_howl', () => northOf(b));
      }
      const oo = e.data.orbOrder as OrbOrder | undefined;
      if (oo) watchOrbOrder(w, b, e, oo);
    },
    onCastStart: kharos
      ? {
          kharos_rend: (w, b, e, cur) => {
            after(b, 1.32, () => {
              if (!stillCasting(b, cur.castId)) return;
              for (const p of players(w)) if (shapeHits(REND_CONE, { origin: b.pos, facing: cur.aimAngle }, p.pos, p.radius)) addBleed(w, b, e, p);
            });
          },
        }
      : {
          vesh_claw_crimson: (w) => announce(w, 'Vesh\'s claw glows crimson — get far away!', 'warning'),
          vesh_claw_azure: (w) => announce(w, 'Vesh\'s claw glows azure — dodge the shards!', 'warning'),
          vesh_claw_verdant: (w) => announce(w, 'Vesh\'s claw glows green — mind the rings!', 'warning'),
        },
    onCastEnd: {
      hound_last_howl: (w, _b, _e, outcome) => {
        if (outcome === 'staggered' || outcome === 'countered') announce(w, 'The Last Howl is silenced!', 'success');
      },
    },
  };
}

function houndScript(kharos: boolean): BossScript {
  const mechanics: BossMechanic[] = kharos
    ? [
        mech('Orb Order', { bars: 30 }, 'Crimson and azure orbs appear — claim them in alternating order!', startOrbOrder),
        mech('Orb Order II', { bars: 15 }, 'The orbs return — alternate the colours again!', startOrbOrder),
      ]
    : [
        mech('Shard Tempest', { bars: 40 }, 'Shard Tempest! Freezing shards rain down!', (_w, b) => castAt(b, 'vesh_shard_tempest', () => northOf(b))),
        mech('Shard Tempest II', { bars: 20 }, 'Shard Tempest! Watch both waves!', (_w, b) => castAt(b, 'vesh_shard_tempest', () => northOf(b))),
      ];
  return bossScript({
    patterns: kharos
      ? [
          { skill: 'kharos_rend', weight: 3, range: [0, 4] },
          { skill: 'kharos_lunge', weight: 2, range: [3, 12] },
          { skill: 'kharos_teleport_spin', weight: 1.5, range: [4, 20] },
          { skill: 'kharos_roar_jump', weight: 1.2, range: [0, 8] },
          { skill: 'kharos_backflip', weight: 1.5, range: [0, 4] },
          { skill: 'kharos_fear', weight: 1, range: [6, 40], target: 'far' },
          { skill: 'kharos_inferno_breath', weight: 2, range: [0, 9] },
        ]
      : [
          { skill: 'vesh_bite', weight: 3, range: [0, 4] },
          { skill: 'vesh_claw_crimson', weight: 1.2, range: [0, 8] },
          { skill: 'vesh_claw_azure', weight: 1.2, range: [2, 16] },
          { skill: 'vesh_claw_verdant', weight: 1.2, range: [0, 12] },
          { skill: 'vesh_lunge', weight: 2, range: [3, 12] },
          { skill: 'vesh_glacial_bomb', weight: 1.5, range: [0, 25], target: 'random' },
          { skill: 'vesh_backflip', weight: 1.5, range: [0, 4] },
          { skill: 'vesh_teleport_spin', weight: 1.2, range: [4, 20] },
          { skill: 'vesh_blizzard_howl', weight: 1, range: [0, 8] },
        ],
    phaseAt: [50],
    enrageAfter: 600,
    mechanics,
  });
}

// =============================================================== Gate II · Vhal'Zeroth, the Hollow Tyrant

const TYRANT_ATK = 1.0;
const TH = hpScale('raid', TYRANT_ATK);
/** Durability of a plate outside the daze window (the HUD only shows part names). */
const SEALED_PLATE = 1e9;

registerStatus({ id: 'tyrant_armor', name: 'Hollow Plate', kind: 'buff', icon: 'tyrant_armor', description: 'Damage taken -30% per layer. Bait his charges into pillars, then break the plates with weak-point attacks while he is dazed.', stacking: 'stack', maxStacks: 2, mods: { dmgReduction: 0.3 }, color: 0x6a5a8a });
registerStatus({ id: 'tyrant_dazed', name: 'Dazed', kind: 'debuff', icon: 'tyrant_dazed', description: 'Crashed into a pillar: helpless, plates exposed, damage taken +10%.', stacking: 'refresh', mods: { dmgTakenPct: 0.1 }, cc: { disable: true }, color: 0xffe36a });
registerStatus({ id: 'abyssal_ward', name: 'Abyssal Ward', kind: 'buff', icon: 'abyssal_ward', description: 'Protected from the Tyrant\'s Verdict: damage taken -90%. Lost if the first strike hits you.', stacking: 'refresh', mods: { dmgReduction: 0.9 }, color: 0x5a8aff });
registerStatus({ id: 'ward_shattered', name: 'Ward Shattered', kind: 'debuff', icon: 'ward_shattered', description: 'Struck by the Verdict: any ward is negated (damage reduction -90%).', stacking: 'refresh', mods: { dmgReduction: -0.9 }, color: 0x3a2a6a });
registerStatus({ id: 'pillar_shelter', name: 'Sheltered', kind: 'buff', icon: 'pillar_shelter', description: 'Hidden behind a pillar: damage taken -90%.', stacking: 'refresh', mods: { dmgReduction: 0.9 }, color: 0xa0a0c0 });
registerStatus({ id: 'hollow_prison', name: 'Hollow Prison', kind: 'debuff', icon: 'hollow_prison', description: 'Caged: cannot act, and the Sovereign Roar crushes you. Allies must destroy the cage.', stacking: 'strongest', cc: { disable: true }, mods: { dmgTakenPct: 4 }, color: 0x7a2a8a });

const TYRANT_SKILLS: SkillDef[] = [
  bskill({ id: 'tyrant_axe_slam', name: 'Axe Slam', cd: 6, anim: 'Slam', dur: 1.4, actions: [tele(0, rect(9, 3), 0.9, 'red', TH(0.25), { reaction: KD })] }),
  bskill({ id: 'tyrant_cross', name: 'Rock Cross', cd: 13, anim: 'Slam', dur: 2.0, element: 'earth', description: 'A cross of erupting rock; the diagonals are safe.', actions: [tele(0, rect(26, 4, -13), 1.4, 'red', TH(0.35), { reaction: KD }), tele(0, rect(26, 4, -13, 90), 1.4, 'red', TH(0.35), { reaction: KD })] }),
  bskill({ id: 'tyrant_triple_slam', name: 'Triple Slam', cd: 14, anim: 'Slam', dur: 3.1, actions: [tele(0, circle(3.5), 0.9, 'red', TH(0.2), { reaction: KD }, { anchor: 'point' }), tele(0, circle(3.5), 1.7, 'red', TH(0.2), { reaction: KD }, { anchor: 'point' }), tele(0, circle(5.5), 2.6, 'red', TH(0.4), { reaction: KD }, { anchor: 'point' })] }),
  bskill({ id: 'tyrant_axe_storm', name: 'Stormbound Axe', cd: 16, anim: 'Slam', dur: 2.0, element: 'lightning', description: 'Lightning on the axe: stay away.', actions: [tele(0, circle(10), 1.5, 'blue', TH(0.4), { status: [stun(1.5)] })] }),
  bskill({ id: 'tyrant_axe_sweep', name: 'Dark Axe Sweep', cd: 16, anim: 'Attack2', dur: 2.0, description: 'No lightning on the axe: stand in front of him.', actions: [tele(0, cone(12, 300, 180), 1.5, 'red', TH(0.4), { reaction: KD })] }),
  bskill({ id: 'tyrant_anchor', name: 'Hollow Anchor', cd: 12, anim: 'Attack2', dur: 2.3, actions: [proj(0.6, { speed: 18, range: 18, radius: 0.8, count: 3, spreadDeg: 36, hit: { shape: circle(0.8), coef: TH(0.15), reaction: KD } }), tele(0, ring(4, 12), 1.8, 'red', TH(0.3), { reaction: kb(3) })] }),
  bskill({ id: 'tyrant_portal_charge', name: 'Portal Charge', cd: 15, anim: 'Charge', dur: 2.3, actions: [tele(0, rect(30, 4), 1.3, 'red', 0), move(1.3, 26, 0.6, { ignoreCollision: true }), hits(1.32, 4, 0.14, rect(3, 4), TH(0.35), { reaction: KD })] }),
  bskill({ id: 'tyrant_counter_slash', name: 'Hollow Cleave', cd: 12, anim: 'Attack', dur: 2.0, tags: ['counterable'], actions: [counterWindow(0.5, 1.0), tele(0, cone(8, 120), 1.5, 'red', TH(0.45), { reaction: KD })] }),
  bskill({ id: 'tyrant_roar', name: 'Tyrant\'s Roar', cd: 18, anim: 'Roar', dur: 1.7, actions: [tele(0, circle(12), 1.2, 'blue', TH(0.1), { status: [{ id: 'silence', duration: 2 }] })] }),
  bskill({ id: 'tyrant_smash_shockwave', name: 'Smash Shockwave', cd: 15, anim: 'Slam', dur: 2.3, description: 'Jump into the first explosion to avoid the shockwave.', actions: [tele(0, circle(4), 0.8, 'red', TH(0.15), { reaction: KD }, { anchor: 'point' }), tele(0, ring(4, 18), 1.8, 'red', TH(0.45), { reaction: KD }, { anchor: 'point' })] }),
  // Mechanic-only.
  bskill({ id: 'tyrant_hollow_charge', name: 'Hollow Charge', cd: 0, anim: 'Charge', dur: 2.8, description: 'A charge that shatters pillars — and dazes him.', actions: [tele(0, rect(34, 4.5), 1.4, 'red', 0), move(1.4, 30, 1.1), hits(1.42, 5, 0.2, rect(3, 4.5), TH(0.35), { reaction: KD })] }),
  bskill({ id: 'tyrant_verdict', name: 'Tyrant\'s Verdict', cd: 0, anim: 'Slam', dur: 5.2, tags: ['oneshot'], description: 'Two axe strikes; the second is lethal unless an Abyssal Ward protects you. The first strike shatters wards.', actions: [tele(0, cone(16, 140), 1.6, 'red', TH(0.35), { reaction: KD, status: [{ id: 'ward_shattered', duration: 4 }] }), tele(0, circle(50), 4.6, 'purple', TH(ONE_SHOT), { reaction: KD })] }),
  bskill({ id: 'tyrant_hollow_tide', name: 'Hollow Tide', cd: 0, anim: 'Roar', dur: 4.0, tags: ['oneshot'], description: 'A vast shockwave: hide behind a pillar.', actions: [tele(0, circle(50), 3.0, 'yellow', TH(1.8), { reaction: kb(8) }), tele(0, cone(20, 30), 3.4, 'red', TH(0.5), { reaction: KD })] }),
  bskill({ id: 'tyrant_ring_break_outer', name: 'Ring Break', cd: 0, anim: 'Slam', dur: 3.0, description: 'The outer ring of the arena collapses into the Hollow.', actions: [tele(0, ring(17, 40), 2.2, 'red', TH(0.6), { reaction: KD }, { anchor: 'point' }), hazard(0, ring(17, 40), 2.2, 900, 1, TH(0.3), 'red', { status: [burn()] }, { anchor: 'point' })] }),
  bskill({ id: 'tyrant_ring_break_inner', name: 'Ring Break', cd: 0, anim: 'Slam', dur: 3.0, description: 'The next ring collapses.', actions: [tele(0, ring(12, 40), 2.2, 'red', TH(0.6), { reaction: KD }, { anchor: 'point' }), hazard(0, ring(12, 40), 2.2, 900, 1, TH(0.3), 'red', { status: [burn()] }, { anchor: 'point' })] }),
  bskill({ id: 'tyrant_counter_1', name: 'Hollow Lunge I', cd: 0, anim: 'Charge', dur: 2.3, tags: ['counterable'], actions: [counterWindow(0.6, 1.1), tele(0, cone(9, 100), 1.8, 'red', TH(0.5), { reaction: KD })] }),
  bskill({ id: 'tyrant_counter_2', name: 'Hollow Lunge II', cd: 0, anim: 'Charge', dur: 2.1, tags: ['counterable'], actions: [counterWindow(0.5, 1.0), tele(0, cone(10, 120), 1.6, 'red', TH(0.6), { reaction: KD })] }),
  bskill({ id: 'tyrant_counter_3', name: 'Hollow Lunge III', cd: 0, anim: 'Charge', dur: 2.4, tags: ['counterable'], actions: [counterWindow(0.6, 1.1), tele(0, circle(10), 1.9, 'red', TH(1.0), { reaction: KD })] }),
  bskill({ id: 'tyrant_rampage', name: 'Hollow Rampage', cd: 0, anim: 'Charge', dur: 5.0, tags: ['oneshot'], description: 'The punishment for a failed counter check.', actions: [move(0.8, 12, 0.4), hits(0.82, 3, 0.13, rect(3, 4), TH(0.3), { reaction: KD }), move(1.8, -12, 0.4), move(2.8, 12, 0.4), hits(2.82, 3, 0.13, rect(3, 4), TH(0.3), { reaction: KD }), tele(3.0, circle(8), 1.2, 'red', TH(ONE_SHOT), { reaction: launch(1.5, 1.2) })] }),
  wipeCast('tyrant_abyssal_crush', 'Abyssal Crush', 12, TH(ONE_SHOT)),
  bskill({ id: 'tyrant_sovereign_roar', name: 'Sovereign Roar', cd: 0, anim: 'Roar', dur: 2.0, description: 'Crushes every caged player.', actions: [tele(0, circle(50), 1.5, 'red', TH(0.4), { reaction: KD })] }),
];

const pillarDef = (id: string, name: string): MonsterDef => ({
  id, name, grade: 'normal', model: 'pillar_ruin', scale: 1.6, radius: 1.3, tint: 0x4a4050,
  stats: (l) => monsterStats(l, 'normal', { hp: 50 }), skills: [], brain: 'static', aggroRange: 0, leash: 99, xp: 0, loot: null,
  params: { helper: true },
});

const ABYSSAL_ORB: MonsterDef = {
  id: 'abyssal_orb', name: 'Abyssal Orb', grade: 'minion', model: 'fx_orb', scale: 1, radius: 0.1, tint: 0x4a7aff,
  stats: (l) => monsterStats(l, 'minion'), skills: [], brain: 'none', aggroRange: 0, leash: 99, xp: 0, loot: null,
  params: { helper: true, pickupRadius: 1.3 },
};

const HOLLOW_CAGE: MonsterDef = {
  id: 'hollow_cage', name: 'Hollow Cage', grade: 'elite', model: 'fx_cage', scale: 1, radius: 0.3, tint: 0x7a2a8a,
  stats: (l) => monsterStats(l, 'elite', { hp: 0.8 }), skills: [], brain: 'static', aggroRange: 0, leash: 99, xp: 0, loot: null,
  params: { helper: true },
};

const VHALZEROTH: MonsterDef = {
  id: 'vhalzeroth', name: 'Vhal\'Zeroth', title: 'Warlord Siege — Gate II', creature: 'Demon', grade: 'raid', model: 'boss_tyrant', scale: 1, radius: 2.4,
  stats: (l) => monsterStats(l, 'raid', { hp: 1.5, atk: TYRANT_ATK }),
  skills: TYRANT_SKILLS, brain: 'boss', aggroRange: 40, leash: 80, xp: 12000, loot: 'siege_g2',
  weakness: 'holy', stagger: 900, hpBars: 180,
  parts: [
    { id: 'plate_outer', name: 'Outer Hollow Plate', durability: 6 },
    { id: 'plate_inner', name: 'Inner Hollow Plate', durability: 6 },
  ],
  params: { arenaRadius: 22, ilvl: 340, gate: 2 },
};

function spawnThronePillars(w: World, b: Actor, e: EncState): void {
  for (const a of DIAGONALS) spawnHelper(w, b, 'throne_pillar', around(e.arena.center, e.arena.radius * 0.5, a * RAD));
}

/** Loop: while the armour holds, Vhal'Zeroth charges the farthest player every few seconds. */
function scheduleHollowCharge(w: World, b: Actor, e: EncState): void {
  after(b, 7, () => {
    if (!b.alive || !e.data.armorActive) return;
    if (helpersOf(w, e, 'throne_pillar').length === 0) {
      const sets = (e.data.pillarSets as number | undefined) ?? 1;
      if (sets >= 2) {
        announce(w, 'The pillars are gone — the Hollow Plate holds.', 'warning');
        return;
      }
      e.data.pillarSets = sets + 1;
      spawnThronePillars(w, b, e);
      announce(w, 'New pillars rise from the throne room floor!', 'info');
    }
    if (!isPending(b, 'tyrant_hollow_charge')) {
      castAt(b, 'tyrant_hollow_charge', () => {
        const p = farthestPlayer(w, b.pos);
        return p ? { ...p.pos } : null;
      });
    }
    scheduleHollowCharge(w, b, e);
  });
}

function pillarCollision(w: World, b: Actor, e: EncState, pillar: Actor, castId: number): void {
  e.interruptedCast = castId;
  b.displacement = null;
  w.interruptCast(b);
  w.kill(pillar, b);
  after(b, 1.5, () => despawn(w, pillar.id));
  w.grantStatus(b, { id: 'tyrant_dazed', duration: 5 }, b.id);
  w.grantStatus(b, { id: 'corroded', duration: 5 }, b.id);
  // The plates open at full durability for the length of the daze.
  for (const p of b.monster!.parts) if (!p.broken) p.value = p.max;
  announce(w, 'Vhal\'Zeroth crashes into the pillar — break his plates!', 'success');
}

function chainStep(w: World, b: Actor, e: EncState, k: number, outcome: CastOutcome): void {
  if (e.data.chain !== k) return;
  if (outcome === 'completed') {
    e.data.chain = 0;
    announce(w, 'The counter check failed — Hollow Rampage!', 'warning');
    castAt(b, 'tyrant_rampage', () => {
      const p = farthestPlayer(w, b.pos);
      return p ? { ...p.pos } : northOf(b);
    });
    return;
  }
  if (outcome === 'dead' || outcome === 'interrupted') {
    e.data.chain = 0;
    return;
  }
  if (k >= 3) {
    e.data.chain = 0;
    announce(w, 'All three counters landed — the Tyrant reels!', 'success');
    return;
  }
  e.data.chain = k + 1;
  announce(w, `Counter ${k}/3 — again!`, 'success');
  castAt(b, `tyrant_counter_${k + 1}`, playerPoint(w, b, b.monster!.targetId));
}

interface PrisonEntry {
  player: number;
  cage: number;
  freed: boolean;
}

function tyrantMechanics(hard: boolean): BossMechanic[] {
  const list: BossMechanic[] = [
    mech('Armour Break', { bars: 160 }, 'Hollow Plate! Bait Vhal\'Zeroth\'s charges into the pillars, then break his armour!', (w, b, e) => {
      e.data.armorActive = true;
      e.data.pillarSets = 1;
      setStacks(w, b, 'tyrant_armor', 2, 1e9, b.id);
      spawnThronePillars(w, b, e);
      scheduleHollowCharge(w, b, e);
    }),
    mech('Tyrant\'s Verdict', { bars: 130 }, 'Tyrant\'s Verdict! Grab an Abyssal Orb and dodge the first strike!', (w, b, e) => {
      const n = Math.max(3, w.partySize + 1);
      const base = w.rng.range(0, Math.PI * 2);
      for (let i = 0; i < n; i++) spawnHelper(w, b, 'abyssal_orb', around(e.arena.center, e.arena.radius * 0.6, base + (i / n) * Math.PI * 2));
      e.data.warded = [];
      e.data.orbsActive = true;
      castAt(b, 'tyrant_verdict', playerPoint(w, b, randomPlayer(w)?.id ?? null), 9);
    }),
    mech('Hollow Tide', { bars: 110 }, 'Hollow Tide! Hide behind a pillar!', (w, b, e) => {
      for (const a of DIAGONALS) spawnHelper(w, b, 'hug_pillar', around(e.arena.center, e.arena.radius * 0.45, a * RAD));
      castAt(b, 'tyrant_hollow_tide', playerPoint(w, b, randomPlayer(w)?.id ?? null), 5);
    }),
    mech('Ring Break', { bars: 85 }, 'The outer ring is collapsing — move inward!', (_w, b, e) => castAt(b, 'tyrant_ring_break_outer', e.arena.center)),
    mech('Counter Check', { bars: 65 }, 'Counter Check! Counter all three Hollow Lunges!', (w, b, e) => {
      e.data.chain = 1;
      castAt(b, 'tyrant_counter_1', playerPoint(w, b, b.monster!.targetId));
    }),
    mech('Abyssal Crush', { bars: 50 }, 'Abyssal Crush! Stagger Vhal\'Zeroth before the cast completes!', (_w, b) => {
      resetStagger(b, 0.7);
      castAt(b, 'tyrant_abyssal_crush', () => northOf(b));
    }),
    mech('Ring Break II', { bars: 35 }, 'Another ring collapses — gather in the centre!', (_w, b, e) => castAt(b, 'tyrant_ring_break_inner', e.arena.center)),
  ];
  if (hard) {
    list.push(
      mech('Imprisonment', { bars: 20 }, 'Imprisonment! Destroy the Hollow Cages before the Sovereign Roar!', (w, b, e) => {
        const ps = w.rng.shuffle(players(w));
        const n = ps.length >= 2 ? Math.max(1, Math.floor(ps.length / 2)) : 0;
        const entries: PrisonEntry[] = ps.slice(0, n).map((p) => {
          w.grantStatus(p, { id: 'hollow_prison', duration: 12 }, b.id);
          const cage = spawnHelper(w, b, 'hollow_cage', { ...p.pos }, { add: true });
          cage.monster!.immunePush = true;
          return { player: p.id, cage: cage.id, freed: false };
        });
        e.data.prison = entries;
        castAt(b, 'tyrant_sovereign_roar', () => northOf(b), 10);
      }),
    );
  }
  return list;
}

function tyrantScript(hard: boolean): BossScript {
  return bossScript({
    patterns: [
      { skill: 'tyrant_axe_slam', weight: 3, range: [0, 7] },
      { skill: 'tyrant_cross', weight: 1.5, range: [0, 12] },
      { skill: 'tyrant_triple_slam', weight: 1.5, range: [0, 18], target: 'random' },
      { skill: 'tyrant_axe_storm', weight: 1.2, range: [0, 8] },
      { skill: 'tyrant_axe_sweep', weight: 1.2, range: [0, 10] },
      { skill: 'tyrant_anchor', weight: 1.5, range: [3, 18] },
      { skill: 'tyrant_portal_charge', weight: 1.2, range: [5, 26] },
      { skill: 'tyrant_counter_slash', weight: 2, range: [0, 7] },
      { skill: 'tyrant_roar', weight: 1, range: [0, 10] },
      { skill: 'tyrant_smash_shockwave', weight: 1.5, range: [0, 16], target: 'random' },
    ],
    phaseAt: [50],
    enrageAfter: 720,
    mechanics: tyrantMechanics(hard),
  });
}

const TYRANT_ENCOUNTER: EncounterDef = {
  clearOnDeath: ['hollow_prison', 'abyssal_ward', 'ward_shattered', 'pillar_shelter'],
  watch: (w, b, e) => {
    // Armour plates are sealed (unbreakable) unless he is dazed by a pillar; each daze opens
    // them at full durability and progress is lost when it ends. Each broken plate removes a layer.
    const dazed = b.hasStatus('tyrant_dazed');
    const plates = b.monster!.parts;
    for (const p of plates) {
      if (p.broken) continue;
      if (e.data.armorActive && dazed) p.value = Math.min(p.value, p.max);
      else p.value = SEALED_PLATE;
    }
    if (e.data.armorActive) {
      const want = Math.max(0, 2 - plates.filter((p) => p.broken).length);
      if (want !== stacksOf(b, 'tyrant_armor')) {
        setStacks(w, b, 'tyrant_armor', want, 1e9, b.id);
        if (want === 0) {
          e.data.armorActive = false;
          for (const pl of helpersOf(w, e, 'throne_pillar')) w.kill(pl, b);
          announce(w, 'The Hollow Plate shatters!', 'success');
        } else announce(w, 'A plate breaks!', 'success');
      }
    }
    // Hollow Charge: crashing into a pillar stops him cold.
    const c = b.cast;
    if (c && c.skill.id === 'tyrant_hollow_charge' && b.displacement) {
      const hitPillar = helpersOf(w, e, 'throne_pillar').find((pl) => dist(pl.pos, b.pos) <= b.radius + pl.radius + 0.2);
      if (hitPillar) pillarCollision(w, b, e, hitPillar, c.castId);
    }
    // Abyssal orbs grant wards to players who walk over them (one each).
    if (e.data.orbsActive) {
      const warded = (e.data.warded as number[] | undefined) ?? [];
      for (const orb of helpersOf(w, e, 'abyssal_orb')) {
        const p = players(w).find((pl) => !warded.includes(pl.id) && dist(pl.pos, orb.pos) <= 1.3);
        if (!p) continue;
        warded.push(p.id);
        despawn(w, orb.id);
        w.grantStatus(p, { id: 'abyssal_ward', duration: 25 }, b.id);
      }
      e.data.warded = warded;
    }
    // Hollow Tide: players behind a pillar are sheltered.
    if (e.cur?.skillId === 'tyrant_hollow_tide' && c) {
      const t = e.time - e.cur.start;
      if (t >= 1.8 && t <= 3.4) {
        const pillars = helpersOf(w, e, 'hug_pillar');
        for (const p of players(w)) {
          if (pillars.some((pl) => behindPillar(b.pos, p.pos, pl.pos, pl.radius))) {
            w.grantStatus(p, { id: 'pillar_shelter', duration: 0.5 }, b.id);
            w.grantStatus(p, { id: 'statusImmune', duration: 0.5 }, b.id);
          }
        }
      }
    }
    // Hard: destroyed cages free their prisoner.
    for (const entry of (e.data.prison as PrisonEntry[] | undefined) ?? []) {
      if (entry.freed) continue;
      const cage = w.actors.get(entry.cage);
      if (cage?.alive) continue;
      entry.freed = true;
      const p = w.actors.get(entry.player);
      if (p) {
        w.revokeStatus(p, ['hollow_prison'], b.id);
        announce(w, `${p.name} is freed!`, 'success');
      }
    }
  },
  onCastEnd: {
    tyrant_verdict: (w, _b, e) => {
      e.data.orbsActive = false;
      for (const orb of helpersOf(w, e, 'abyssal_orb')) despawn(w, orb.id);
    },
    tyrant_hollow_tide: (w, b, e) => {
      for (const pl of helpersOf(w, e, 'hug_pillar')) after(b, 0.5, () => despawn(w, pl.id));
    },
    tyrant_counter_1: (w, b, e, o) => chainStep(w, b, e, 1, o),
    tyrant_counter_2: (w, b, e, o) => chainStep(w, b, e, 2, o),
    tyrant_counter_3: (w, b, e, o) => chainStep(w, b, e, 3, o),
    tyrant_abyssal_crush: (w, _b, _e, o) => {
      if (o === 'staggered' || o === 'countered') announce(w, 'Vhal\'Zeroth is staggered — the Abyssal Crush fails!', 'success');
      else if (o === 'completed') announce(w, 'The Abyssal Crush lands…', 'warning');
    },
    tyrant_sovereign_roar: (w, b, e) => {
      for (const entry of (e.data.prison as PrisonEntry[] | undefined) ?? []) {
        const cage = w.actors.get(entry.cage);
        if (cage?.alive) w.kill(cage, null);
        const p = w.actors.get(entry.player);
        if (p) w.revokeStatus(p, ['hollow_prison'], b.id);
        entry.freed = true;
      }
      e.data.prison = [];
    },
  },
};

// =============================================================== Gate III · Phantom of Vhal'Zeroth

const PHANTOM_ATK = 1.1;
const PHH = hpScale('raid', PHANTOM_ATK);
const CLH = hpScale('elite', 1);
export const PHANTOM_STACKS_NORMAL = 4;
export const PHANTOM_STACKS_HARD = 6;

registerStatus({ id: 'phantom_armor', name: 'Phantom Armour', kind: 'buff', icon: 'phantom_armor', description: 'Damage taken -14% per stack. Removed only by countering phantom clones at the arena edge.', stacking: 'stack', maxStacks: PHANTOM_STACKS_HARD, mods: { dmgReduction: 0.14 }, color: 0x9b8cff });
registerStatus({ id: 'phantom_form', name: 'Phantom Form', kind: 'buff', icon: 'phantom_form', description: 'Barely material: damage taken -85%. Counter it instead.', stacking: 'refresh', mods: { dmgReduction: 0.85 }, color: 0xb0a0ff });
registerStatus({ id: 'phantom_final', name: 'Final Wrath', kind: 'buff', icon: 'phantom_final', description: 'Attack speed +25%, move speed +20%.', stacking: 'refresh', mods: { atkSpeedPct: 0.25, moveSpeedPct: 0.2 }, color: 0xff3a8a });
registerStatus({ id: 'lantern_light', name: 'Lantern Light', kind: 'buff', icon: 'lantern_light', description: 'Within a lantern shard\'s glow: damage taken -90%.', stacking: 'refresh', mods: { dmgReduction: 0.9 }, color: 0xffe9a8 });

const PHANTOM_SKILLS: SkillDef[] = [
  bskill({ id: 'phantom_axe_slam', name: 'Spectral Slam', cd: 6, anim: 'Slam', dur: 1.4, element: 'dark', actions: [tele(0, rect(10, 3.2), 0.9, 'red', PHH(0.26), { reaction: KD })] }),
  bskill({ id: 'phantom_cross', name: 'Phantom Cross', cd: 13, anim: 'Slam', dur: 2.0, element: 'dark', actions: [tele(0, rect(28, 4, -14), 1.4, 'red', PHH(0.35), { reaction: KD }), tele(0, rect(28, 4, -14, 90), 1.4, 'red', PHH(0.35), { reaction: KD })] }),
  bskill({ id: 'phantom_scythe_sweep', name: 'Reaper\'s Sweep', cd: 15, anim: 'Attack2', dur: 2.0, element: 'dark', description: 'Everything but his front is reaped.', actions: [tele(0, cone(13, 300, 180), 1.5, 'red', PHH(0.4), { reaction: KD })] }),
  bskill({ id: 'phantom_soul_bolts', name: 'Soul Bolts', cd: 8, anim: 'Attack', dur: 1.2, element: 'dark', actions: [proj(0.6, { speed: 16, range: 22, radius: 0.6, count: 5, spreadDeg: 50, hit: { shape: circle(0.6), coef: PHH(0.12), reaction: { kind: 'flinch' } } })] }),
  bskill({ id: 'phantom_counter_slash', name: 'Phantom Cleave', cd: 12, anim: 'Attack', dur: 2.0, element: 'dark', tags: ['counterable'], actions: [counterWindow(0.5, 1.0), tele(0, cone(8, 120), 1.5, 'red', PHH(0.45), { reaction: KD })] }),
  bskill({ id: 'phantom_portal_slam', name: 'Portal Slam', cd: 14, anim: 'Leap', dur: 1.8, element: 'dark', actions: [teleport(0.5, 20), tele(0.55, circle(6), 0.8, 'red', PHH(0.3), { reaction: launch() })] }),
  bskill({ id: 'phantom_ring_wave', name: 'Hollow Pulse', cd: 14, anim: 'Roar', dur: 2.8, element: 'dark', actions: [tele(0, circle(6), 1.2, 'red', PHH(0.3), { reaction: KD }), tele(0, ring(6, 16), 2.2, 'red', PHH(0.3), { reaction: KD })] }),
  bskill({ id: 'phantom_void_pillars', name: 'Void Pillars', cd: 15, anim: 'Cast', dur: 2.0, element: 'dark', actions: ([[6, 20], [10, 80], [7, 150], [12, 200], [8, 260], [13, 320], [15, 110], [4, 0]] as const).map(([d, a]) => tele(0, polarSquare(d, a, 3.2), 1.2, 'red', PHH(0.25), { reaction: launch(0.8, 0.9) })) }),
  bskill({ id: 'phantom_frenzy', name: 'Wrathful Onslaught', cd: 14, anim: 'Slam', dur: 3.2, element: 'dark', actions: [0, 1, 2, 3, 4].map((i) => tele(0.1 * i, circle(4.5, 3), 0.7 + i * 0.45, 'red', PHH(0.15), { reaction: KD })) }),
  // Mechanic-only.
  bskill({ id: 'phantom_requiem', name: 'Hollow Requiem', cd: 0, anim: 'Roar', dur: 3.0, element: 'dark', tags: ['oneshot', 'wipe'], description: 'Final enrage: the end of the fight.', actions: [tele(0, circle(60), 2.0, 'purple', PHH(3.0), { reaction: KD })] }),
  bskill({ id: 'phantom_eclipse', name: 'Lantern Eclipse', cd: 0, anim: 'Roar', dur: 3.6, element: 'dark', tags: ['oneshot'], description: 'Only a lantern shard\'s glow survives the eclipse.', actions: [tele(0, circle(50), 3.0, 'purple', PHH(ONE_SHOT), { reaction: KD })] }),
];

const PHANTOM_CLONE: MonsterDef = {
  id: 'phantom_clone', name: 'Phantom of Vhal\'Zeroth', grade: 'elite', model: 'boss_tyrant', scale: 0.75, radius: 1.3, tint: 0x9b8cff, creature: 'Demon',
  stats: (l) => monsterStats(l, 'elite', { hp: 40 }),
  skills: [
    bskill({
      id: 'phantom_clone_rush', name: 'Phantom Rush', cd: 0, anim: 'Charge', dur: 4.0, element: 'dark', tags: ['counterable'],
      description: 'Glows blue while gathering, then rushes across the arena. Counter it to shatter one Phantom Armour stack.',
      actions: [counterWindow(0.6, 1.8), tele(0, rect(40, 4), 2.4, 'red', 0), move(2.4, 34, 1.0, { ignoreCollision: true }), hits(2.45, 5, 0.2, rect(3, 4), CLH(0.6), { reaction: kb(9) })],
    }),
  ],
  brain: 'none', aggroRange: 0, leash: 99, xp: 0, loot: null,
  params: { helper: true, material: 'phantom' },
};

const LANTERN_ORB: MonsterDef = {
  id: 'lantern_orb', name: 'Lantern Shard', grade: 'minion', model: 'fx_orb', scale: 1.4, radius: 0.1, tint: 0xffe9a8,
  stats: (l) => monsterStats(l, 'minion'), skills: [], brain: 'none', aggroRange: 0, leash: 99, xp: 0, loot: null,
  params: { helper: true, protectRadius: 3 },
};

const PHANTOM: MonsterDef = {
  id: 'vhalzeroth_phantom', name: 'Phantom of Vhal\'Zeroth', title: 'Warlord Siege — Gate III', creature: 'Demon', grade: 'raid', model: 'boss_tyrant', scale: 1, radius: 2.4, tint: 0x8a7dff,
  stats: (l) => monsterStats(l, 'raid', { hp: 1.3, atk: PHANTOM_ATK }),
  skills: PHANTOM_SKILLS, brain: 'boss', aggroRange: 40, leash: 80, xp: 16000, loot: 'siege_g3',
  weakness: 'holy', stagger: 900, hpBars: 100,
  params: { arenaRadius: 22, ilvl: 340, gate: 3, material: 'phantom' },
};

interface CloneEntry {
  id: number;
  cast: boolean;
  done: boolean;
}

function phantomStacks(b: Actor): number {
  return isHard(b) ? PHANTOM_STACKS_HARD : PHANTOM_STACKS_NORMAL;
}

/** Spawns phantom clones at the arena edge; each rushes the centre after a counterable wind-up. */
export function spawnPhantomClones(w: World, b: Actor, count: number): Actor[] {
  const e = enc(b);
  const clones = (e.data.clones as CloneEntry[] | undefined) ?? [];
  e.data.clones = clones;
  const out: Actor[] = [];
  const base = w.rng.range(0, Math.PI * 2);
  for (let i = 0; i < count; i++) {
    const pos = around(e.arena.center, e.arena.radius - 1.5, base + (i / count) * Math.PI * 2);
    const clone = spawnHelper(w, b, 'phantom_clone', pos, { team: 'monsters', facing: angleTo(pos, e.arena.center) });
    w.grantStatus(clone, { id: 'phantom_form', duration: 1e9 }, b.id);
    clones.push({ id: clone.id, cast: false, done: false });
    out.push(clone);
  }
  return out;
}

function scheduleCloneWave(w: World, b: Actor, e: EncState, delay: number): void {
  after(b, delay, () => {
    if (!b.alive) return;
    if (stacksOf(b, 'phantom_armor') > 0) {
      announce(w, 'Phantoms gather at the edge of the arena — counter them!', 'mechanic');
      spawnPhantomClones(w, b, isHard(b) ? 3 : 2);
    }
    scheduleCloneWave(w, b, e, 28);
  });
}

function watchClones(w: World, b: Actor, e: EncState): void {
  for (const cl of (e.data.clones as CloneEntry[] | undefined) ?? []) {
    if (cl.done) continue;
    const a = w.actors.get(cl.id);
    if (!a) {
      cl.done = true;
      continue;
    }
    if (!a.alive) {
      // Destroyed by damage: no armour removed.
      cl.done = true;
      after(b, 2, () => despawn(w, cl.id));
      continue;
    }
    if ((a.monster?.countered ?? 0) > 0) {
      cl.done = true;
      const left = Math.max(0, stacksOf(b, 'phantom_armor') - 1);
      setStacks(w, b, 'phantom_armor', left, 1e9, b.id);
      w.kill(a, null);
      after(b, 1.5, () => despawn(w, cl.id));
      announce(w, left > 0 ? `Phantom shattered — ${left} armour stacks remain!` : 'The Phantom Armour is gone!', 'success');
      continue;
    }
    if (!cl.cast) {
      if (w.monsterCast(a, 'phantom_clone_rush', { ...e.arena.center })) cl.cast = true;
      continue;
    }
    if (!a.cast) {
      // The rush completed uncountered: the phantom fades.
      cl.done = true;
      despawn(w, cl.id);
    }
  }
}

function phantomMechanics(hard: boolean): BossMechanic[] {
  const legion = (name: string, bars: number): BossMechanic =>
    mech(name, { bars }, 'Phantom Legion! Clones surround the arena — counter them!', (w, b) => {
      spawnPhantomClones(w, b, hard ? 4 : 3);
    });
  const list: BossMechanic[] = [legion('Phantom Legion', 75), legion('Phantom Legion II', 50), legion('Phantom Legion III', 25)];
  if (hard) {
    list.push(
      mech('Lantern Eclipse', { bars: 15 }, 'Lantern Eclipse! Stand in a lantern shard\'s glow!', (w, b, e) => {
        const base = w.rng.range(0, Math.PI * 2);
        e.data.lanterns = [0, Math.PI].map((o) => spawnHelper(w, b, 'lantern_orb', around(e.arena.center, e.arena.radius * 0.5, base + o)).id);
        castAt(b, 'phantom_eclipse', () => northOf(b), 7);
      }),
    );
  }
  list.push(
    mech('Final Enrage', { bars: 10 }, 'Vhal\'Zeroth\'s phantom enters its final wrath — finish it within a minute!', (w, b) => {
      w.grantStatus(b, { id: 'enraged', duration: 1e9 }, b.id);
      w.grantStatus(b, { id: 'phantom_final', duration: 1e9 }, b.id);
      after(b, 60, () => {
        if (!b.alive) return;
        announce(w, 'Hollow Requiem…', 'warning');
        castAt(b, 'phantom_requiem', () => northOf(b));
      });
    }),
  );
  return list;
}

function phantomScript(hard: boolean): BossScript {
  return bossScript({
    patterns: [
      { skill: 'phantom_axe_slam', weight: 3, range: [0, 8] },
      { skill: 'phantom_cross', weight: 1.5, range: [0, 13] },
      { skill: 'phantom_scythe_sweep', weight: 1.2, range: [0, 11] },
      { skill: 'phantom_soul_bolts', weight: 2, range: [3, 22] },
      { skill: 'phantom_counter_slash', weight: 2, range: [0, 7] },
      { skill: 'phantom_portal_slam', weight: 1.2, range: [5, 22], target: 'far' },
      { skill: 'phantom_ring_wave', weight: 1.5, range: [0, 14] },
      { skill: 'phantom_void_pillars', weight: 1.5, range: [0, 16] },
      { skill: 'phantom_frenzy', weight: 2, range: [0, 8], phases: [3] },
    ],
    phaseAt: [60, 25],
    enrageAfter: 720,
    mechanics: phantomMechanics(hard),
  });
}

const PHANTOM_ENCOUNTER: EncounterDef = {
  clearOnDeath: ['lantern_light'],
  init: (w, b, e) => {
    setStacks(w, b, 'phantom_armor', phantomStacks(b), 1e9, b.id);
    scheduleCloneWave(w, b, e, 10);
  },
  watch: (w, b, e) => {
    watchClones(w, b, e);
    const lanterns = ((e.data.lanterns as number[] | undefined) ?? []).map((id) => w.actors.get(id)).filter((a): a is Actor => !!a?.alive);
    if (lanterns.length > 0 && every(e, 'lantern', 0.2)) {
      for (const p of players(w)) if (lanterns.some((l) => dist(l.pos, p.pos) <= 3)) w.grantStatus(p, { id: 'lantern_light', duration: 0.6 }, b.id);
    }
  },
  onCastEnd: {
    phantom_eclipse: (w, _b, e) => {
      for (const id of (e.data.lanterns as number[] | undefined) ?? []) despawn(w, id);
      e.data.lanterns = [];
    },
  },
};

// =============================================================== exports

const THRONE_PILLAR = pillarDef('throne_pillar', 'Throne Pillar');
const HUG_PILLAR = pillarDef('hug_pillar', 'Obsidian Pillar');

export const SIEGE_BOSS_IDS = ['hellhound_kharos', 'hellhound_vesh', 'vhalzeroth', 'vhalzeroth_phantom'] as const;

export const SIEGE: MonsterDef[] = [
  KHAROS,
  VESH,
  VHALZEROTH,
  PHANTOM,
  hardVariant(KHAROS, { partner: 'hellhound_vesh_hard' }),
  hardVariant(VESH, { partner: 'hellhound_kharos_hard' }),
  hardVariant(VHALZEROTH),
  hardVariant(PHANTOM),
  houndOrb('hound_orb_crimson', 'Crimson Orb', 0xff3a2a),
  houndOrb('hound_orb_azure', 'Azure Orb', 0x3a7aff),
  THRONE_PILLAR,
  HUG_PILLAR,
  ABYSSAL_ORB,
  HOLLOW_CAGE,
  PHANTOM_CLONE,
  LANTERN_ORB,
];

export function registerSiegeScripts(): void {
  for (const hard of [false, true]) {
    const sfx = hard ? '_hard' : '';
    registerBossScript(`hellhound_kharos${sfx}`, houndScript(true));
    registerBossScript(`hellhound_vesh${sfx}`, houndScript(false));
    registerBossScript(`vhalzeroth${sfx}`, tyrantScript(hard));
    registerBossScript(`vhalzeroth_phantom${sfx}`, phantomScript(hard));
  }
  registerEncounter(['hellhound_kharos', 'hellhound_kharos_hard'], houndEncounter(true));
  registerEncounter(['hellhound_vesh', 'hellhound_vesh_hard'], houndEncounter(false));
  registerEncounter(['vhalzeroth', 'vhalzeroth_hard'], TYRANT_ENCOUNTER);
  registerEncounter(['vhalzeroth_phantom', 'vhalzeroth_phantom_hard'], PHANTOM_ENCOUNTER);
}
